/**
 * The bell's per-operator unread counts over the call log.
 *
 * Its own file rather than another export on `calls.ts`, which is already long
 * enough that finding anything in it is a search. The split is also honest about
 * what these two endpoints are: everything in `calls.ts` is about calls, and this
 * is about one person's reading of them.
 *
 * What each stream actually selects is decided on the server, in
 * `modules/crm-calls/lib/seen.ts`. Nothing here re-states it - a second copy of
 * that query is exactly how the sidebar badge came to disagree with the folder
 * it opens.
 */
import { api } from "./api";

export type CallSeenStream = "calls.missed" | "calls.voicemail";

export const CALL_SEEN_STREAMS: readonly CallSeenStream[] = ["calls.missed", "calls.voicemail"];

export interface CallUnseen {
  /** Inbound calls nobody took, excluding the ones that left a message. */
  missed: number;
  /** Inbound calls with a recorded message. */
  voicemail: number;
  /** Where each cursor now sits, ISO. Not drawn anywhere; useful in a console
   *  when a count looks wrong. */
  seen: Record<CallSeenStream, string>;
}

export function getCallUnseen(): Promise<CallUnseen> {
  return api.get<CallUnseen>("/admin/crm/calls/unseen");
}

/** Move the bookmark to now. Omitting the list means every stream, which is what
 *  the bell's own button wants. The response is a fresh read, so a caller can
 *  use it directly instead of polling again. */
export function markCallsSeen(streams?: readonly CallSeenStream[]): Promise<CallUnseen> {
  return api.post<CallUnseen>("/admin/crm/calls/seen", streams ? { streams } : {});
}
